import { AxiosError, AxiosHeaders, create as axiosCreate } from 'axios';
import { AttachmentEntityType } from 'interfaces';

const csrfErrorCodes = ['FST_CSRF_INVALID_TOKEN', 'FST_CSRF_MISSING_SECRET'];

const apiClient = axiosCreate({
  baseURL: process.env.REACT_APP_API || 'http://127.0.0.1:8080',
  withCredentials: true,
});

const getCsrf = async (): Promise<void> => {
  const { data } = await apiClient.get<{ token: string }>('/auth/csrf');

  sessionStorage.setItem('_cto', data.token);
};

apiClient.interceptors.request.use(
  (config) => {
    const headers = new AxiosHeaders({
      'content-type': config.headers['Content-Type'] || 'application/json',
      accept: 'application/json;multipart/form-data',
      'csrf-token': sessionStorage.getItem('_cto'),
    });

    return {
      ...config,
      headers,
    };
  },
  (error: AxiosError) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ code: string }>) => {
    if (error.code === 'ERR_CANCELED' || error.code === 'ERR_FAILED') {
      return Promise.reject(error.message);
    }

    if (!error.response || error.response.status === 401) {
      document.location.href = '/login';
    }

    const initialRequestConfig = error.config!;
    const errorCode = error.response?.data.code ?? '';

    if (csrfErrorCodes.includes(errorCode)) {
      await getCsrf();

      return apiClient(initialRequestConfig);
    }

    return Promise.reject(error.response?.data);
  },
);

export const generatePresignedURLs = async (
  id: string,
  attachments: File[],
  entityType: AttachmentEntityType,
): Promise<
  {
    fileName: string;
    objectKey: string;
    presignedUrl: string;
  }[]
> => {
  const { data } = await apiClient.post('/presigned-urls', {
    entityID: id,
    entityType,
    entityFiles: attachments.map(({ name, type, size }) => ({
      name,
      type,
      size,
    })),
  });

  return data;
};

export const uploadFilesByType = async (
  id: string,
  attachments: File[],
  entityType: AttachmentEntityType,
): Promise<void> => {
  const entityTypeToIdMap = {
    recognitions: 'recognitionID',
    broadcasts: 'broadcastID',
    tasks: 'taskID',
  };

  try {
    const presignedURLs = await generatePresignedURLs(
      id,
      attachments,
      entityType,
    );

    await apiClient.post('/attachments/create', {
      attachments: presignedURLs.map(({ fileName, objectKey }) => {
        const attachment = attachments.find((a) => a.name === fileName)!;

        return {
          [entityTypeToIdMap[entityType]]: id,
          name: fileName,
          objectKey,
          type: attachment.type,
          size: attachment.size,
        };
      }),
    });

    const uploadPromises = presignedURLs.map(
      async ({ presignedUrl, fileName }) => {
        const attachment = attachments.find((a) => a.name === fileName)!;

        return apiClient.put(presignedUrl, attachment, {
          headers: {
            'Content-Type': attachment.type,
          },
        });
      },
    );

    const uploadResults = await Promise.allSettled(uploadPromises);

    const failedUploads = uploadResults.filter(
      (res) => res.status === 'rejected',
    );

    if (failedUploads.length) {
      await apiClient.post('/attachments/delete', {
        objectKeys: presignedURLs.map(({ objectKey }) => objectKey),
      });
      throw new Error('Failed to upload all files');
    }

    return;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export default apiClient;
